import React, { useState, useEffect } from "react";
import "./styles.css";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  Divider,
  Field,
  Input,
  Link,
  Toast,
  ToastBody,
  Toaster,
  ToastIntent,
  ToastTitle,
  ToastTrigger,
  Tooltip,
  useId,
  useToastController,
} from "@fluentui/react-components";
import Dropzone from "react-dropzone";
import UploadIcon from "../../../assets/uploadicon.svg";
import MaterialReactTable, { MRT_ColumnDef } from "material-react-table";
import { Delete16Regular, Edit16Filled } from "@fluentui/react-icons";
import apiServices from "../../../service";
import FormikInputRequest from "../../../service/hook/useFormik";
import OverlayLoader from "../../../components/OverlayLoader";
import { useDispatch } from "react-redux";
import ASCButton from "../../../components/Button";
import DynamicDropzone from "../../../components/DynamicDropzone/Dropzone";
import SystemManagerDeletePopup from "../../../components/SystemManagerPopup/SystemManagerDeletePopup";
import { getUserId } from "../../../utils";

const LetterTemplates = () => {
  const [rawAddFiles, setRawAddFiles] = useState<File[]>([]);
  const [scannedAddFiles, setScannedAddFiles] = useState<any[]>([]);
  const [rawEditFiles, setRawEditFiles] = useState<File[]>([]);
  const [scannedEditFiles, setScannedEditFiles] = useState<any[]>([]);
  const userId = getUserId();
  const isAddScanning = scannedAddFiles.some((f: any) => f.status === "uploading" || f.status === "scanning");
  const isEditScanning = scannedEditFiles.some((f: any) => f.status === "uploading" || f.status === "scanning");
  const [mode, setMode] = useState<"view" | "add" | "edit">("view");
  const [showAdd, setShowAdd] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [templateListing, setTemplateListing] = useState<any>([]);
  const initialValues = { title: "", description: "", files: "" };
  const [loading, setLoading] = useState<boolean>(false);
  const [cuLoading, setCULoading] = useState<boolean>(false);
  const [sampleTemplate, setSampleTemplate] = useState<any>([]);
  const [openSampleTemplate, setOpenSampleTemplate] = useState<boolean>(false);
  const [sampleLoading, setSampleLoading] = useState<boolean>(false);
  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [deletePopup, setDeletePopup] = useState<boolean>(false);
  const [rowToDelete, setRowToDelete] = useState<any>(null);
  const dispatch = useDispatch();
  const toasterId = useId("toaster");
  const { dispatchToast } = useToastController(toasterId);

  const notify = (message: any, type?: any) =>
    dispatchToast(
      <Toast>
        <ToastTitle
          action={
            <ToastTrigger>
              <Link>Dismiss</Link>
            </ToastTrigger>
          }
        >
          {message}
        </ToastTitle>
      </Toast>,
      { intent: type ? type : "success" },
    );

  const templateList = async () => {
    setLoading(true);
    try {
      const res = await apiServices.TemplateEngine.getLetterTemplate();
      setTemplateListing(res.data);
      return res.data as any[];
    } catch (error: any) {
      notify(error?.response?.data?.message || error?.message || "Failed to fetch templates", "error");
      console.error("Error fetching the template list:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    templateList();
  }, []);

  const formikData = FormikInputRequest({
    initialValues,
    onSubmitFn: async (values: any) => {
      if (showAdd) {
        const { description, title } = values;
        const objectIds = scannedAddFiles.filter((f: any) => f.objectId).map((f: any) => f.objectId).join(",");
        const formData = new FormData();
        if (objectIds) formData.append("objectIds", objectIds);
        try {
          setCULoading(true);
          const response = await apiServices.TemplateEngine.templateLetterUpload({ description, title, files: formData });
          if (!response.data.isError) {
            notify(response.data.message, "success");
          } else {
            notify(response.data.error.message, "error");
          }
          await templateList();
          setRawAddFiles([]);
          setScannedAddFiles([]);
          formikData.resetForm();
          toggleAddMode(false);
          setMode("view");
        } catch (error: any) {
          console.error("Upload Failed:", error);
          notify(error?.response?.data?.message || error?.message || "Upload failed", "error");
        } finally {
          setCULoading(false);
        }
      } else if (showEdit) {
        if (!isEditDirty) return;
        const objectIds = scannedEditFiles.filter((f: any) => f.objectId).map((f: any) => f.objectId).join(",");
        const formData = new FormData();
        if (objectIds) formData.append("objectIds", objectIds);
        const editedCode = selectedRow.code;
        try {
          setCULoading(true);
          const payload = {
            title: values.title.trim(),
            description: values.description || "",
            code: editedCode,
            type: "Letter" as const,
            files: formData,
          };
          const response = await apiServices.TemplateEngine.updateTemplateLetter(payload);
          notify(response.data.data.message, "success");
          // Show the updated values in the details panel
          const templates = await templateList();
          setSelectedRow(templates?.find((t: any) => t.code === editedCode) ?? null);
          setRawEditFiles([]);
          setScannedEditFiles([]);
          formikData.resetForm();
          setShowEdit(false);
          setMode("view");
        } catch (error: any) {
          // Keep the dialog open so the user can correct the input or cancel
          console.error("Update Failed:", error);
          notify(error?.response?.data?.message || error?.message || "Update failed", "error");
        } finally {
          setCULoading(false);
        }
      }
    },
    validate: (values: any) => {
      const errors: any = {};

      if (showAdd || showEdit) {
        const title = (values.title || "").trim();
        if (!title) {
          errors.title = "Title is required";
        } else if (title.length > 50) {
          // Reports.Name is VARCHAR(50)
          errors.title = "Title must be 50 characters or less";
        }
      }

      // File upload validation (only for "Add" mode)
      if (showAdd && !values.files) {
        errors.files = "File upload is required";
      }

      return errors;
    },
  });

  function onChangeValue(key: string, value: any) {
    formikData.setFieldValue(key, value);
  }

  // Edit: a new file only counts once it has passed the virus scan
  const isEditFileReady = scannedEditFiles.some((f: any) => f.status === "success");
  const isEditDirty =
    !!selectedRow &&
    ((formikData.values.title || "").trim() !== (selectedRow.title || "") ||
      (formikData.values.description || "") !== (selectedRow.description || "") ||
      isEditFileReady);

  const columns: MRT_ColumnDef<any>[] = [
    { accessorKey: "title", header: "Title" },
    { accessorKey: "description", header: "Description" },
    {
      accessorKey: "signedUrl",
      header: "Template",
      Cell: ({ row }: any) => {
        const extractFileName = (filePath: string): string => {
          const match = filePath.match(/[^/]+$/);
          return match ? match[0] : "Unknown File";
        };
        const fileName = extractFileName(row.original.templateFilePath);

        return (
          <a
            href={row.original.signedUrl}
            style={{ textDecoration: "none", color: "black" }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {fileName}
          </a>
        );
      },
    },
    { accessorKey: "status", header: "Status" },
  ];

  useEffect(() => {
    if (!selectedRow && templateListing.length > 0) {
      setSelectedRow(templateListing[0]);
    }
  }, [templateListing]);

  const handleRowSelection = (row: any) => {
    setSelectedRow(row.original);
  };

  const toggleAddMode = (isAddMode: boolean) => {
    setShowAdd(isAddMode);
    // setMode(isAddMode ? "add" : "view");
  };

  const handleEdit = (row: any) => {
    setSelectedRow(row.original);
    // Pre-fill the form with the template's current values
    formikData.resetForm({
      values: {
        title: row.original.title || "",
        description: row.original.description || "",
        files: "",
      },
    });
    setRawEditFiles([]);
    setScannedEditFiles([]);
    setShowEdit(true);
  };

  const handleDelete = (row: any) => {
    setRowToDelete(row.original);
    setDeletePopup(true);
  };

  const confirmDelete = async () => {
    setDeletePopup(false);
    if (!rowToDelete) return;
    setLoading(true);

    const id = rowToDelete.templateFilePath;

    try {
      const res = await apiServices.TemplateEngine.deleteLetterTemplate(id);
      notify(res.data.message, "success");
      // Clear the details panel if it was showing the deleted template;
      // the listing effect then selects the first remaining row.
      if (selectedRow?.templateFilePath === id) {
        setSelectedRow(null);
      }
      await templateList();
    } catch (error: any) {
      notify(error?.response?.data?.message || error?.message || "Failed to delete template", "error");
      console.error("Error deleting the template:", error);
    } finally {
      setRowToDelete(null);
      setLoading(false);
    }
  };

  const sampleTemplates = async () => {
    setSampleLoading(true);
    try {
      const res = await apiServices.TemplateEngine.getSampleLetter();
      setSampleTemplate(res.data);
    } catch (error) {
      console.error("Error fetching the template list:", error);
    } finally {
      setSampleLoading(false);
    }
  };
  useEffect(() => {
    sampleTemplates();
  }, []);
  const columnsData: MRT_ColumnDef<any>[] = [
    {
      accessorKey: "Name",
      header: "Title",
    },
    {
      accessorKey: "Description",
      header: "Description",
    },
    {
      accessorKey: "SignedUrl",
      header: "Template Link",
      Cell: ({ row }: any) => {
        const extractFileName = (filePath: string): string => {
          const match = filePath.match(/[^/]+$/);
          return match ? match[0] : "Unknown File";
        };
        const fileName = extractFileName(row.original.TemplateFilePath);

        return (
          <a
            href={row.original.SignedUrl}
            style={{
              textDecoration: "none",
              color: "black",
              cursor: "pointer",
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {fileName}
          </a>
        );
      },
    },
  ];

  return (
    <>
      <OverlayLoader isLoading={loading} />
      <Toaster
        toasterId={toasterId}
        position="top-end"
        pauseOnHover
        pauseOnWindowBlur
        timeout={1000}
      />

      <div className="container">
        {mode === "view" && (
          <div className="add-btn">
            <div>
              <ASCButton
                shape="rounded"
                appearance="primary"
                className="asc-button-primary"
                onClick={() => toggleAddMode(true)}
                label="Add New"
              />
            </div>
            <div>
              {" "}
              <ASCButton
                shape="rounded"
                appearance="secondary"
                onClick={() => {
                  sampleTemplates();
                  setOpenSampleTemplate(true);
                }}
                label="Get Sample Templates"
              />
            </div>
          </div>
        )}

        {templateListing.length === 0 && mode === "view" && !showAdd && (
          <div className="notification">
            <span className="notification-font">
              There are no active records. Click 'Add New' to create a new
              record.
            </span>
          </div>
        )}

        {mode === "view" && templateListing.length > 0 && (
          <div className="admin_container_flex">
            {selectedRow && (
              <div className="email-template">
                <div className="section">
                  <div>
                    <span className="view-bold">Title :</span>
                  </div>
                  <div>{selectedRow?.title}</div>
                </div>
                <div className="section">
                  <div>
                    <span className="view-bold">Description :</span>
                  </div>
                  <div>{selectedRow.description}</div>
                </div>
                <div className="section">
                  <div>
                    <span className="view-bold">Template :</span>
                  </div>
                  <div>
                    <a
                      href={selectedRow?.signedUrl}
                      download={
                        selectedRow?.templateFilePath
                          ? selectedRow.templateFilePath.split("/").pop()
                          : undefined
                      }
                      style={{ textDecoration: "none", color: "#3498db" }}
                    >
                      {selectedRow?.templateFilePath
                        ? selectedRow.templateFilePath.split("/").pop()
                        : "Download File"}
                    </a>
                  </div>
                </div>
                <div className="section">
                  <div>
                    <span className="view-bold">Status :</span>
                  </div>
                  <div>{selectedRow?.status}</div>
                </div>
              </div>
            )}
            <MaterialReactTable
              data={templateListing}
              columns={columns}
              muiTableBodyCellProps={{
                style: { padding: "4px 8px", fontSize: "0.875rem" },
              }}
              muiTableContainerProps={{
                style: {
                  minHeight: "265px",
                  maxHeight: "265px",
                  overflow: "auto",
                },
              }}
              enableRowActions
              initialState={{
                columnPinning: { right: ["mrt-row-actions"] },
                pagination: { pageSize: 5, pageIndex: 0 },
              }}
              muiTableBodyRowProps={({ row }) => ({
                onClick: () => handleRowSelection(row),
                sx: {
                  cursor: "pointer",
                  backgroundColor:
                    selectedRow && row.original.id === selectedRow.id
                      ? "white"
                      : "transparent ",
                },
              })}
              displayColumnDefOptions={{
                "mrt-row-actions": {
                  header: "Actions",
                  muiTableHeadCellProps: { align: "center" },
                  muiTableBodyCellProps: { align: "center" },
                },
              }}
              renderRowActions={({ row }) => (
                <div>
                  <Tooltip content="Edit" relationship="label">
                    <ASCButton
                      shape="rounded"
                      // S3-only files (no Reports row, so no code) cannot be updated
                      disabled={!row?.original?.code}
                      appearance="subtle"
                      icon={<Edit16Filled />}
                      style={{ color: "var(--button-color)" }}
                      onClick={() => handleEdit(row)}
                    />
                  </Tooltip>
                  <Tooltip content="Delete" relationship="label">
                    <ASCButton
                      shape="rounded"
                      appearance="subtle"
                      style={{ color: "red" }}
                      icon={<Delete16Regular />}
                      onClick={() => handleDelete(row)}
                    />
                  </Tooltip>
                </div>
              )}
            />
          </div>
        )}
      </div>

      <SystemManagerDeletePopup
        deletePopup={deletePopup}
        setDeletePopup={(open: boolean) => {
          setDeletePopup(open);
          if (!open) setRowToDelete(null);
        }}
        deleteData={confirmDelete}
      />

      <Dialog modalType="alert" open={openSampleTemplate}>
        <DialogSurface>
          <DialogBody>
            <span style={{ fontWeight: 600, fontSize: "16px" }}>
              Sample Templates{" "}
            </span>
            <DialogContent>
              <OverlayLoader isLoading={sampleLoading} />
              <MaterialReactTable
                columns={columnsData}
                data={sampleTemplate}
                manualPagination
                enableGlobalFilter={true}
                enableColumnActions={false}
                enableColumnFilters={true}
                muiTableBodyCellProps={{
                  style: { padding: "4px 8px", fontSize: "0.875rem" },
                }}
                enableMultiRowSelection={false}
                enableFullScreenToggle={false}
                muiTablePaginationProps={{
                  showFirstButton: false,
                  showLastButton: false,
                }}
              />
            </DialogContent>
            <DialogActions>
              <ASCButton
                shape="rounded"
                size="medium"
                onClick={() => {
                  setOpenSampleTemplate(false);
                }}
                label="Close"
              />
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog modalType="alert" open={showAdd}>
        <DialogSurface>
          <DialogBody>
            <DialogContent>
              <OverlayLoader isLoading={loading} />
              <div className="email-template-add-popup">
                <span style={{ fontWeight: 600, fontSize: "15px" }}>
                  Add New
                </span>
                {/* <Divider /> */}
                <div className="section">
                  <div>
                    <Field
                      label={
                        <span>
                          Title<span style={{ color: "red" }}>*</span>
                        </span>
                      }
                    >
                      <Input
                        name="title"
                        value={formikData.values.title}
                        className="input-width-popup"
                        onChange={(ev: any) => {
                          onChangeValue("title", ev.target.value);
                          // dispatch(setFormData());
                        }}
                      />
                      {formikData.errors.title && formikData.touched.title && (
                        <div style={{ color: "red", fontSize: "12px" }}>
                          {typeof formikData.errors.title === "string"
                            ? formikData.errors.title
                            : ""}
                        </div>
                      )}
                    </Field>
                  </div>
                </div>
                <div className="section">
                  <div>
                    <Field label="Description">
                      <Input
                        className="input-width-popup"
                        name="description"
                        value={formikData.values.description}
                        onChange={(ev: any) => {
                          onChangeValue("description", ev.target.value);
                          // dispatch(setFormData());
                        }}
                      />
                    </Field>
                  </div>
                </div>
                <div className="section">
                  <div>
                    <Field
                      label={
                        <span>
                          File Upload
                          <span style={{ color: "red" }}>* &nbsp;( </span>
                          <span style={{ color: "red", fontWeight: 600 }}>
                            .docx only )
                          </span>
                        </span>
                      }
                    >
                      <div style={{ marginTop: "4px" }}>
                        <Dropzone
                          multiple={false}
                          disabled={rawAddFiles.length > 0}
                          accept={{ "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"] }}
                          onDrop={(acceptedFiles: File[]) => {
                            if (acceptedFiles.length > 0) {
                              setRawAddFiles([acceptedFiles[0]]);
                            }
                          }}
                        >
                          {({ getRootProps, getInputProps }) => (
                            <div
                              {...getRootProps()}
                              className="file-drops"
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                opacity: rawAddFiles.length > 0 ? 0.4 : 1,
                                pointerEvents: rawAddFiles.length > 0 ? "none" : "auto",
                                cursor: rawAddFiles.length > 0 ? "not-allowed" : "pointer",
                              }}
                            >
                              <input {...getInputProps()} />
                              <img src={UploadIcon} width="35px" alt="upload-icon" />
                              <p style={{ margin: "6px 0 0 0" }}>Click here to upload file</p>
                            </div>
                          )}
                        </Dropzone>
                        {rawAddFiles.length > 0 && (
                          <DynamicDropzone
                            style={{ marginTop: "10px" }}
                            multiple={false}
                            externalFiles={rawAddFiles}
                            onFilesChange={(files: any) => {
                              setScannedAddFiles(files);
                              const successFile = files.find((f: any) => f.status === "success");
                              if (successFile) {
                                formikData.setFieldValue("files", successFile.file);
                              } else if (files.length === 0) {
                                formikData.setFieldValue("files", "");
                                setRawAddFiles([]);
                              }
                            }}
                            userId={userId}
                          />
                        )}
                      </div>
                      {formikData.errors.files && formikData.touched.files && (
                        <div style={{ color: "red", fontSize: "12px" }}>
                          {typeof formikData.errors.files === "string"
                            ? formikData.errors.files
                            : ""}
                        </div>
                      )}
                    </Field>
                  </div>
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <div className="action-button">
                <ASCButton
                  shape="rounded"
                  appearance="primary"
                  className="asc-button-primary"
                  onClick={() => {
                    formikData.validateForm().then((errors) => {
                      if (Object.keys(errors).length === 0) {
                        formikData.handleSubmit();
                      }
                    });
                  }}
                  disabled={
                    isAddScanning ||
                    !formikData.isValid ||
                    formikData.isSubmitting ||
                    (showAdd && !formikData.values.files) ||
                    (!showAdd && !formikData.dirty)
                  }
                  label={isAddScanning ? "Scanning..." : cuLoading ? "Saving..." : "Save & Upload"}
                />
                <ASCButton
                  shape="rounded"
                  appearance="secondary"
                  onClick={() => {
                    setRawAddFiles([]);
                    setScannedAddFiles([]);
                    formikData.setFieldValue("files", "");
                    toggleAddMode(false);
                    formikData.resetForm();
                  }}
                  label="Cancel"
                />
              </div>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {selectedRow && (
        <Dialog modalType="alert" open={showEdit}>
          <DialogSurface>
            <DialogBody>
              <DialogContent>
                <div className="email-template-add-popup">
                  <span style={{ fontWeight: 600, fontSize: "15px" }}>
                    Edit
                  </span>
                  {/* <Divider /> */}
                  <div className="section">
                    <div>
                      <Field
                        label={
                          <span>
                            Title<span style={{ color: "red" }}>*</span>
                          </span>
                        }
                      >
                        <Input
                          name="title"
                          value={formikData.values.title}
                          className="input-width-popup"
                          onChange={(ev: any) =>
                            onChangeValue("title", ev.target.value)
                          }
                        />
                        {formikData.errors.title && (
                          <div style={{ color: "red", fontSize: "12px" }}>
                            {typeof formikData.errors.title === "string"
                              ? formikData.errors.title
                              : ""}
                          </div>
                        )}
                      </Field>
                    </div>
                  </div>
                  <div className="section">
                    <div>
                      <Field label="Description">
                        <Input
                          className="input-width-popup"
                          name="description"
                          value={formikData.values.description}
                          onChange={(ev: any) =>
                            onChangeValue("description", ev.target.value)
                          }
                        />
                      </Field>
                    </div>
                  </div>
                  <div className="section">
                    <div>
                      <Field
                        label={
                          <span>
                            File Upload
                            <span style={{ color: "red", fontWeight: 600 }}>
                              &nbsp;( .docx only )
                            </span>
                          </span>
                        }
                      >
                        <div style={{ marginTop: "4px", fontSize: "12px" }}>
                          {rawEditFiles.length > 0 ? "Replacing" : "Current file"}:{" "}
                          <a
                            href={selectedRow.signedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#3498db",
                              textDecoration: rawEditFiles.length > 0 ? "line-through" : "none",
                            }}
                          >
                            {selectedRow.templateFilePath?.split("/").pop()}
                          </a>
                        </div>
                        <div style={{ marginTop: "4px" }}>
                          <Dropzone
                            multiple={false}
                            disabled={rawEditFiles.length > 0}
                            accept={{ "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"] }}
                            onDrop={(acceptedFiles: File[]) => {
                              if (acceptedFiles.length > 0) {
                                setRawEditFiles([acceptedFiles[0]]);
                                formikData.setFieldValue("files", acceptedFiles[0]);
                              }
                            }}
                          >
                            {({ getRootProps, getInputProps }) => (
                              <div
                                {...getRootProps()}
                                className="file-drops"
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  opacity: rawEditFiles.length > 0 ? 0.4 : 1,
                                  pointerEvents: rawEditFiles.length > 0 ? "none" : "auto",
                                  cursor: rawEditFiles.length > 0 ? "not-allowed" : "pointer",
                                }}
                              >
                                <input {...getInputProps()} />
                                <img src={UploadIcon} width="35px" alt="upload-icon" />
                                <p style={{ margin: "6px 0 0 0" }}>Click here to upload file</p>
                              </div>
                            )}
                          </Dropzone>
                          {rawEditFiles.length > 0 && (
                            <DynamicDropzone
                              style={{ marginTop: "10px" }}
                              multiple={false}
                              externalFiles={rawEditFiles}
                              onFilesChange={(files: any) => {
                                setScannedEditFiles(files);
                                const successFile = files.find((f: any) => f.status === "success");
                                if (successFile) {
                                  formikData.setFieldValue("files", successFile.file);
                                } else if (files.length === 0) {
                                  formikData.setFieldValue("files", "");
                                  setRawEditFiles([]);
                                }
                              }}
                              userId={userId}
                            />
                          )}
                        </div>
                        {formikData.errors.files && formikData.touched.files && (
                          <div style={{ color: "red", fontSize: "12px" }}>
                            {typeof formikData.errors.files === "string"
                              ? formikData.errors.files
                              : ""}
                          </div>
                        )}
                      </Field>
                    </div>
                  </div>
                </div>
              </DialogContent>
              <DialogActions>
                <div className="action-button">
                  <ASCButton
                    shape="rounded"
                    appearance="primary"
                    className="asc-button-primary"
                    onClick={() => {
                      formikData.handleSubmit();
                    }}
                    disabled={
                      isEditScanning ||
                      cuLoading ||
                      !formikData.isValid ||
                      // a chosen file must pass the scan before it can be saved
                      (rawEditFiles.length > 0 && !isEditFileReady) ||
                      !isEditDirty
                    }
                    label={isEditScanning ? "Scanning..." : cuLoading ? "Updating..." : "Save"}
                  />
                  <ASCButton
                    shape="rounded"
                    appearance="secondary"
                    onClick={() => {
                      setRawEditFiles([]);
                      setScannedEditFiles([]);
                      formikData.setFieldValue("files", "");
                      setShowEdit(false);
                      formikData.resetForm();
                    }}
                    label="Cancel"
                  />
                </div>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </>
  );
};

export default LetterTemplates;
